import { Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";

export function fmtMins(m) {
  if (!m) return "—";
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
}

function heat(m, max) {
  if (!m) return "bg-slate-50 text-slate-300";
  const r = max ? m / max : 0;
  if (r > 0.75) return "bg-emerald-600 text-white";
  if (r > 0.5) return "bg-emerald-500 text-white";
  if (r > 0.25) return "bg-emerald-300 text-emerald-950";
  return "bg-emerald-100 text-emerald-900";
}

function dayLabel(d) {
  const dt = new Date(d + "T00:00:00");
  return { dow: dt.toLocaleDateString("en-IN", { weekday: "short" }).slice(0, 2), dd: dt.getDate(), mon: dt.toLocaleDateString("en-IN", { month: "short" }), weekend: [0, 6].includes(dt.getDay()) };
}

export function DailyTimeGrid({ data, isLoading }) {
  if (isLoading) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const days = data?.days || [];
  const members = data?.members || [];
  const max = Math.max(1, ...members.flatMap((m) => m.minutes));
  const colTotals = days.map((_, i) => members.reduce((s, m) => s + (m.minutes[i] || 0), 0));
  const labels = days.map(dayLabel);
  return (
    <Card className="border-slate-200 overflow-x-auto" data-testid="daily-time-grid">
      <table className="text-xs border-separate border-spacing-0.5 min-w-max">
        <thead>
          <tr>
            <th className="sticky left-0 bg-white text-left px-2 py-1 font-semibold text-slate-500 uppercase tracking-wider">User</th>
            <th className="px-2 py-1 font-semibold text-slate-500 uppercase tracking-wider text-right">Total</th>
            {labels.map((l, i) => (
              <th key={days[i]} className={`px-1 py-1 font-medium text-center leading-tight ${l.weekend ? "text-slate-400" : "text-slate-600"} ${days[i] === data?.today ? "text-indigo-700" : ""}`} title={days[i]}>
                <div>{l.dow}</div><div className="font-mono">{l.dd}</div>{(i === 0 || l.dd === 1) && <div className="text-[10px] text-slate-400">{l.mon}</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.user_id} data-testid={`daily-row-${m.user_id}`}>
              <td className="sticky left-0 bg-white px-2 py-1 font-medium text-slate-800 whitespace-nowrap max-w-[180px] truncate" title={m.name}>{m.name}</td>
              <td className="px-2 py-1 text-right font-mono whitespace-nowrap text-slate-700">{fmtMins(m.total)}</td>
              {m.minutes.map((v, i) => (
                <td key={days[i]} className={`h-7 min-w-[34px] text-center font-mono rounded ${heat(v, max)} ${labels[i].weekend && !v ? "bg-slate-100/80" : ""}`} title={`${m.name} · ${days[i]} · ${fmtMins(v)}`}>
                  {v ? (v >= 60 ? `${(v / 60).toFixed(1)}h` : `${v}m`) : ""}
                </td>
              ))}
            </tr>
          ))}
          <tr className="font-semibold" data-testid="daily-totals-row">
            <td className="sticky left-0 bg-slate-50 px-2 py-1 text-slate-700">Team total</td>
            <td className="px-2 py-1 text-right font-mono bg-slate-50 whitespace-nowrap">{fmtMins(colTotals.reduce((a, b) => a + b, 0))}</td>
            {colTotals.map((v, i) => <td key={days[i]} className="px-1 py-1 text-center font-mono bg-slate-50 text-slate-700 whitespace-nowrap">{v ? (v >= 60 ? `${(v / 60).toFixed(1)}h` : `${v}m`) : "—"}</td>)}
          </tr>
        </tbody>
      </table>
      {!members.length && <p className="p-6 text-sm text-slate-400 text-center">No usage recorded in this range.</p>}
    </Card>
  );
}

export function MemberDayBars({ member, days, today }) {
  if (!member) return <p className="text-xs text-slate-400 px-3 py-2">No usage recorded in this range.</p>;
  const pairs = days.map((d, i) => [d, member.minutes[i] || 0]).filter(([, v]) => v > 0).reverse();
  if (!pairs.length) return <p className="text-xs text-slate-400 px-3 py-2">No usage recorded in this range.</p>;
  const max = Math.max(...pairs.map(([, v]) => v));
  return (
    <div className="px-3 py-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3" data-testid={`daily-bars-${member.user_id}`}>
      {pairs.map(([d, v]) => {
        const l = dayLabel(d);
        return (
          <div key={d} className="flex items-center gap-2 text-xs">
            <span className={`w-20 shrink-0 font-mono ${d === today ? "text-indigo-700 font-semibold" : l.weekend ? "text-slate-400" : "text-slate-600"}`}>{l.dow} {l.dd} {l.mon}</span>
            <div className="flex-1 h-2.5 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-emerald-500 rounded" style={{ width: `${Math.max(4, (v / max) * 100)}%` }} /></div>
            <span className="w-14 text-right font-mono text-slate-700">{fmtMins(v)}</span>
          </div>
        );
      })}
    </div>
  );
}
